"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { blogsApi } from "@/lib/api/services/blogs.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BookOpen, Plus, Clock } from "lucide-react";
import Link from "next/link";

interface Article {
  id: string;
  title: string;
  description: string;
  category: string;
  author: string;
  createdAt: string;
}

export default function KnowledgeHubPage() {
  const { data: articles, isLoading } = useQuery({
    queryKey: ["knowledge-hub"],
    queryFn: async () => {
      const data = await blogsApi.list();
      return ((data as unknown as { data?: Article[] }).data ?? []) as Article[];
    },
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground">Knowledge Hub</h1>
          <p className="mt-1 text-sm text-muted-foreground">Browse articles, guides, and insights.</p>
        </div>
        <Button asChild size="sm">
          <Link href="/knowledge-hub">
            <Plus className="mr-2 h-4 w-4" /> Browse
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-3 p-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-muted/10 animate-pulse shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-3/4 bg-muted/10 rounded animate-pulse" />
                    <div className="h-3 w-1/2 bg-muted/10 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : !articles || articles.length === 0 ? (
            <div className="py-16 text-center">
              <BookOpen className="mx-auto h-10 w-10 text-muted-foreground/30" />
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground">No articles yet</h3>
              <p className="mt-1 text-sm text-muted-foreground">Be the first to share knowledge.</p>
            </div>
          ) : (
            <div className="divide-y divide-border/60">
              {articles.map((article) => (
                <Link key={article.id} href={`/blogs/${article.id}`} className="flex items-center gap-3 p-4 transition-colors hover:bg-muted/10">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted/10 text-muted-foreground">
                    <BookOpen className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{article.title}</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{article.description}</p>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                    <Clock className="h-3 w-3" />
                    <span>{article.createdAt}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
